import { Button, Rating, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { showings } from '@prisma/client';
import ShowingTime from '../../components/ShowingTime/ShowingTime';
import { isAfter } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import showingsService from '../../api/services/showings';
import { useAppDispatch } from '../../store/store';
import { fetchShowings } from '../../store/slices/showings';

const improvementLabels: Record<string, string> = {
  priceAdjustment: 'price adjustment',
  updatedFeatures: 'updated interior features',
  betterStaging: 'better staging',
  location: 'different location',
  largerLayout: 'larger layout',
};

const parseFeedback = (feedback: string) => {
  // Try parsing as JSON (partial or complete survey)
  try {
    const parsed = JSON.parse(feedback);
    const entries: { label: string; value: string }[] = [];
    if (parsed.overallImpression) entries.push({ label: 'Overall Impression', value: parsed.overallImpression });
    if (parsed.homeSuitability) entries.push({ label: 'Home Suitability', value: parsed.homeSuitability });
    if (parsed.bestFeatures) entries.push({ label: 'Best Features', value: parsed.bestFeatures });
    if (parsed.improvements) {
      const items = Object.entries(parsed.improvements)
        .filter(([key, val]) => val === true && key !== 'other' && key !== 'otherImprovementText')
        .map(([key]) => improvementLabels[key] || key);
      if (parsed.improvements.other && parsed.improvements.otherImprovementText) {
        items.push(parsed.improvements.otherImprovementText);
      }
      if (items.length > 0) entries.push({ label: 'Suggested Improvements', value: items.join(', ') });
    }
    if (parsed.purchaseLikelihood) entries.push({ label: 'Purchase Likelihood', value: parsed.purchaseLikelihood });
    if (parsed.additionalComments) entries.push({ label: 'Additional Comments', value: parsed.additionalComments });
    if (parsed.complete === false) entries.push({ label: 'Status', value: 'Partial response' });
    return entries;
  } catch {
    // Legacy plain-text feedback
  }

  const entries: { label: string; value: string }[] = [];
  for (const line of feedback.split('\n')) {
    const colonIdx = line.indexOf(':');
    if (colonIdx === -1) continue;
    const label = line.slice(0, colonIdx).trim();
    const value = line.slice(colonIdx + 1).trim();
    if (value && value !== 'None') entries.push({ label, value });
  }

  // Feedback texted in as a reply has no labels at all, so show it as written
  // rather than rendering an empty block.
  if (entries.length === 0 && feedback.trim()) {
    return [{ label: 'Feedback', value: feedback.trim() }];
  }

  return entries;
};

interface Props {
  showing?: showings;
}

const Showing: React.FC<Props> = ({ showing }) => {
  const isPast = isAfter(
    new Date(),
    new Date(showing?.proposedTime ?? new Date())
  );
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const isBuyer = currentUser?.id === id;
  const [feedback, setFeedback] = useState(showing?.feedback ?? '');
  const [saved, setSaved] = useState(!!showing?.feedback);
  const dispatch = useAppDispatch();

  const saveFeedback = async (id: string, feedback: string) => {
    await showingsService.patch(id, { feedback });
    setSaved(true);
    dispatch(fetchShowings());
  };

  const handleFeedbackChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFeedback(event.target.value);
  };

  return (
    <Grid container spacing={2}>
      <Grid xs={12}>
        <Stack direction="column" spacing={1} pt={1}>
          <ShowingTime showing={showing} />
          <Stack direction="row" alignItems="center">
            <Typography variant="body1" fontWeight={600}>
              Feedback
            </Typography>
          </Stack>
          {isBuyer && isPast && !saved ? (
            <Stack direction="row" spacing={1}>
              <TextField
                multiline
                minRows={1}
                maxRows={5}
                fullWidth
                value={feedback}
                onChange={handleFeedbackChange}
                placeholder="Enter any showing feedback you have."
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => saveFeedback(showing?.id ?? '', feedback)}
              >
                Save
              </Button>
            </Stack>
          ) : showing?.feedback ? (
            <Stack spacing={1.5}>
              {parseFeedback(showing.feedback).map(({ label, value }) =>
                label === 'Overall Impression' ? (
                  <Stack key={label} direction="row" alignItems="center" spacing={1}>
                    <Rating value={Number(value) || 0} readOnly size="small" />
                    <Typography variant="body2" color="text.secondary">
                      {value}/5
                    </Typography>
                  </Stack>
                ) : (
                  <Stack key={label}>
                    <Typography variant="body2" fontWeight={600}>
                      {label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {value}
                    </Typography>
                  </Stack>
                )
              )}
            </Stack>
          ) : (
            <Typography variant="body2">None</Typography>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default Showing;
